import CustomSync from '@/components/custom-sync';
import { GiftCardTransactionsFragment } from '@/gql/generated';
import useNetwork from '@/hooks/use-network';
import useVerifySync from '@/hooks/use-verify-sync';
import { dateToPreviousDate } from '@/utils/constant';
import { useContext, useEffect, useState } from 'react';
import { TZDate } from 'react-day-picker';
import { useNavigate } from 'react-router-dom';
import { useAuthenticatedProvider } from '../../_provider/authenticated-provider';
import { TransactionWarehouseContext } from '../../_provider/transaction-warehouse-provider';
import TransactionAggregate from './_components/transaction-aggregate';
import TransactionHeader from './_components/transaction-header';
import TransactionTable from './_components/transaction-table';
import GiftCardTransactionDetailModal from './[id]/page';

const GiftCardTransactionPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticatedProvider();
  const {
    queryResult,
    totalGiftCardSold,
    totalPreviousGiftCardSold,
    totalRedeem,
    totalPreviousRedeem,
    totalAddFund,
    totalPreviousAddFund,
    event,
  } = useContext(TransactionWarehouseContext);
  const { verifySynced, isSynced } = useVerifySync();
  const whmcsId = user.whmcs_id;
  const { isOnline } = useNetwork();

  const [startDate, setStartDate] = useState<TZDate>(
    new TZDate(new Date(), user.time_zone!),
  );
  const [endDate, setEndDate] = useState<TZDate>(
    new TZDate(new Date(), user.time_zone!),
  );

  const [selectedTransaction, setSelectedTransaction] = useState<
    GiftCardTransactionsFragment | undefined
  >();

  const dateTimeFilter = dateToPreviousDate(startDate, endDate);

  const onNavigate = (path: string) => {
    navigate(path);
  };

  useEffect(() => {
    event.setStartDate(startDate);
    event.setEndDate(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    verifySynced(new Date(queryResult?.dataUpdatedAt ?? 0));
  }, [queryResult?.dataUpdatedAt]);

  useEffect(() => {
    if (!isSynced) {
      event.refetchAll();
    }
  }, []);

  return (
    <div className="flex size-full flex-col gap-12 bg-gray-white">
      <div className="flex-none">
        <TransactionHeader
          startDate={startDate}
          endDate={endDate}
          timeZone={user.time_zone!}
          selectedValue={dateTimeFilter.type}
          onBack={onNavigate}
          onChange={(start, end) => {
            setStartDate(start);
            setEndDate(end);
          }}
        />
      </div>
      <div className="flex-none">
        <TransactionAggregate
          startDate={startDate}
          endDate={endDate}
          whmcsId={whmcsId}
          totalGiftCardSold={totalGiftCardSold}
          totalPreviousGiftCardSold={totalPreviousGiftCardSold}
          totalRedeem={totalRedeem}
          totalPreviousRedeem={totalPreviousRedeem}
          totalAddFund={totalAddFund}
          totalPreviousAddFund={totalPreviousAddFund}
        />
      </div>
      <div className="grow overflow-hidden">
        <TransactionTable
          startDate={startDate}
          endDate={endDate}
          whmcsId={whmcsId}
          queryResult={queryResult}
          handleClick={setSelectedTransaction}
        />
      </div>
      {!isOnline && (
        <CustomSync
          isSynced={isSynced}
          isSyncing={queryResult?.isFetching ?? false}
          onSync={() => {
            queryResult?.refetch();
          }}
        />
      )}

      {selectedTransaction && (
        <GiftCardTransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(undefined)}
        />
      )}
    </div>
  );
};

export default GiftCardTransactionPage;
